import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Divider,
  Paper,
  Alert,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Cancel,
  Schedule,
  SwapHoriz,
  ManageAccounts,
  Refresh,
  Payment,
  Print,
  ContentCopy,
  Link as LinkIcon,
  WhatsApp,
} from '@mui/icons-material';
import { substitutionService, type SubstitutionRequest, type SubstitutionAnalytics, parseVehicleName } from '@/services/substitutionService';
import { toast } from 'react-hot-toast';
import { getApiBaseUrl } from '@/config/environment';

export const VehicleSubstitutionPage: React.FC = () => {
  // State management
  const [selectedTab, setSelectedTab] = useState(0);
  const [openNewSubstitution, setOpenNewSubstitution] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [substitutionRequests, setSubstitutionRequests] = useState<SubstitutionRequest[]>([]);
  const [analytics, setAnalytics] = useState<SubstitutionAnalytics | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0
  });

  // Dialog state for creating new substitution
  const [newSubstitutionData, setNewSubstitutionData] = useState({
    bookingId: '',
    substituteVehicleId: '',
    reason: ''
  });

  // Payment link and receipt state
  const [selectedRequest, setSelectedRequest] = useState<SubstitutionRequest | null>(null);
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState<{
    paymentUrl: string;
    customerName: string;
    customerEmail: string;
    amount: number;
    isExisting: boolean;
  } | null>(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);

  // Get admin user ID
  const getAdminUserId = (): string => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.id || user.userId || 'admin-default';
      } catch (e) {
      }
    }
    return 'admin-default';
  };

  // Fetch substitution requests
  const fetchSubstitutionRequests = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const status = selectedTab === 0 ? 'all' :
                    selectedTab === 1 ? 'pending' :
                    selectedTab === 2 ? 'accepted' : 'declined';

      const response = await substitutionService.getSubstitutionRequests({
        status,
        page: pagination.page,
        limit: pagination.limit,
        sortBy: 'created_at',
        sortOrder: 'desc'
      });

      if (response.success && response.data) {
        setSubstitutionRequests(response.data.requests);
        setPagination(response.data.pagination);
      } else {
        setError(response.error || 'Failed to load substitution requests');
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('An error occurred while loading requests');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch analytics
  const fetchAnalytics = async () => {
    try {
      const response = await substitutionService.getAnalytics();
      if (response.success && response.data) {
        setAnalytics(response.data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  };

  // Load data on mount and when filters change
  useEffect(() => {
    fetchSubstitutionRequests();
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]); // Only re-fetch when tab changes

  // Handle admin override (approve/reject)
  const handleOverride = async (requestId: string, action: 'approve' | 'reject', reason: string) => {
    const adminId = getAdminUserId();

    try {
      const response = await substitutionService.overrideSubstitutionRequest(
        requestId,
        action,
        adminId,
        reason
      );

      if (response.success) {
        toast.success(`Request ${action}d successfully`);
        fetchSubstitutionRequests(); // Refresh list
      } else {
        toast.error(response.error || `Failed to ${action} request`);
      }
    } catch (err) {
      console.error(`Error ${action}ing request:`, err);
      toast.error(`An error occurred while ${action}ing the request`);
    }
  };

  // Handle payment link generation
  const handleGeneratePaymentLink = async (request: SubstitutionRequest) => {
    try {
      const loadingToast = toast.loading('Generating payment link...');
      const response = await fetch(`${getApiBaseUrl()}/api/admin/bookings/${request.bookingId}/generate-payment-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ callbackUrl: `${window.location.origin}/payment-success` })
      });
      if (!response.ok) throw new Error('Failed to generate payment link');
      const result = await response.json();
      toast.dismiss(loadingToast);
      await navigator.clipboard.writeText(result.data.paymentUrl);
      setPaymentLinkData(result.data);
      setPaymentLinkDialogOpen(true);
      toast.success('Payment link generated!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate payment link');
    }
  };

  // Handle print receipt
  const handlePrintReceipt = (request: SubstitutionRequest) => {
    setSelectedRequest(request);
    setReceiptDialogOpen(true);
  };

  const handlePrint = () => {
    if (!selectedRequest) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print receipts');
      return;
    }

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0
      }).format(amount || 0);
    };

    const receiptHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${selectedRequest.bookingId}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #1976d2;
          }
          .company-name {
            font-size: 32px;
            font-weight: 700;
            color: #1976d2;
            margin-bottom: 8px;
          }
          .receipt-title {
            font-size: 20px;
            color: #666;
            margin-bottom: 8px;
          }
          .booking-ref {
            font-size: 18px;
            font-weight: 600;
            color: #333;
          }
          .invoice-meta {
            display: flex;
            justify-content: space-between;
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px solid #e0e0e0;
          }
          .invoice-meta-item {
            text-align: left;
          }
          .invoice-meta-label {
            font-size: 12px;
            color: #999;
            text-transform: uppercase;
            margin-bottom: 4px;
          }
          .invoice-meta-value {
            font-size: 14px;
            font-weight: 600;
            color: #333;
          }
          .section {
            margin-bottom: 30px;
          }
          .section-title {
            font-size: 16px;
            font-weight: 600;
            color: #1976d2;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e0e0e0;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #f0f0f0;
          }
          .info-label {
            color: #666;
            font-weight: 500;
          }
          .info-value {
            font-weight: 600;
            text-align: right;
          }
          .subtotal-row {
            display: flex;
            justify-content: space-between;
            padding: 12px 0;
            margin-top: 8px;
            border-top: 1px solid #e0e0e0;
            font-weight: 600;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 16px 0;
            margin-top: 8px;
            border-top: 2px solid #333;
            font-size: 18px;
            font-weight: 700;
            background-color: #f8f9fa;
            margin: 0 -16px;
            padding: 16px;
          }
          .cost-item {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px dotted #e0e0e0;
          }
          .cost-item-label {
            color: #666;
          }
          .cost-item-value {
            font-weight: 600;
          }
          .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 16px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
          }
          .status-accepted {
            background-color: #d4edda;
            color: #155724;
          }
          .status-pending {
            background-color: #fff3cd;
            color: #856404;
          }
          .status-declined {
            background-color: #f8d7da;
            color: #721c24;
          }
          .vehicle-box {
            padding: 16px;
            background-color: #f8f9fa;
            border-radius: 8px;
            margin-bottom: 12px;
          }
          .arrow-icon {
            text-align: center;
            font-size: 24px;
            color: #1976d2;
            margin: 16px 0;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e0e0e0;
            text-align: center;
            color: #666;
            font-size: 14px;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">PolarRide</div>
          <div class="receipt-title">Tax Invoice / Receipt - Vehicle Substitution</div>
          <div class="booking-ref">Booking Reference: ${selectedRequest.bookingId}</div>
          <div class="invoice-meta">
            <div class="invoice-meta-item">
              <div class="invoice-meta-label">Invoice Number</div>
              <div class="invoice-meta-value">INV-${selectedRequest.bookingId}</div>
            </div>
            <div class="invoice-meta-item">
              <div class="invoice-meta-label">Invoice Date</div>
              <div class="invoice-meta-value">${new Date().toLocaleDateString('en-US', { dateStyle: 'medium' })}</div>
            </div>
            <div class="invoice-meta-item">
              <div class="invoice-meta-label">Request Status</div>
              <div class="invoice-meta-value">
                <span class="status-badge ${
                  selectedRequest.status === 'accepted' ? 'status-accepted' :
                  selectedRequest.status === 'declined' ? 'status-declined' : 'status-pending'
                }">${selectedRequest.status.toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Substitution Details</div>
          <div class="info-row">
            <span class="info-label">Request ID:</span>
            <span class="info-value">${selectedRequest.id}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Requested Date:</span>
            <span class="info-value">${new Date(selectedRequest.createdAt).toLocaleDateString('en-US', { dateStyle: 'full' })}</span>
          </div>
          ${selectedRequest.reason ? `
          <div class="info-row">
            <span class="info-label">Reason:</span>
            <span class="info-value">${selectedRequest.reason}</span>
          </div>` : ''}
          ${selectedRequest.requestedBy ? `
          <div class="info-row">
            <span class="info-label">Requested By:</span>
            <span class="info-value">${selectedRequest.requestedBy.name || 'N/A'} (${selectedRequest.requestedByType})</span>
          </div>` : ''}
        </div>

        <div class="section">
          <div class="section-title">Customer Details</div>
          <div class="info-row">
            <span class="info-label">Name:</span>
            <span class="info-value">${selectedRequest.guest.name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Email:</span>
            <span class="info-value">${selectedRequest.guest.email}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Phone:</span>
            <span class="info-value">${selectedRequest.guest.phone}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Vehicle Substitution</div>
          <div class="vehicle-box">
            <div style="font-weight: 600; margin-bottom: 8px; color: #666;">Original Vehicle</div>
            <div style="font-size: 18px; font-weight: 700; color: #333;">${selectedRequest.originalVehicle.name}</div>
          </div>
          <div class="arrow-icon">↓</div>
          <div class="vehicle-box">
            <div style="font-weight: 600; margin-bottom: 8px; color: #1976d2;">Substitute Vehicle</div>
            <div style="font-size: 18px; font-weight: 700; color: #1976d2;">${selectedRequest.substituteVehicle.name}</div>
          </div>
        </div>

        ${(() => {
          const sub = selectedRequest as any;
          const adjustmentFee = sub.adjustmentFee || sub.priceDifference || 0;

          if (adjustmentFee !== 0) {
            return `
            <div class="section">
              <div class="section-title">Cost Breakdown</div>
              ${adjustmentFee > 0 ? `
              <div style="margin-bottom: 12px; font-weight: 600; color: #666; font-size: 14px;">Customer Pays:</div>
              <div class="info-row">
                <span class="info-label">Vehicle Upgrade Fee</span>
                <span class="info-value">${formatCurrency(adjustmentFee)}</span>
              </div>
              <div style="margin: 16px 0; padding: 16px; background-color: #e3f2fd; border-radius: 4px;">
                <div class="info-row" style="border: none; margin: 0;">
                  <span style="font-weight: 700; font-size: 18px;">Total Amount Due</span>
                  <span style="font-weight: 700; font-size: 18px; color: #1976d2;">${formatCurrency(adjustmentFee)}</span>
                </div>
              </div>` : `
              <div style="margin-bottom: 12px; font-weight: 600; color: #4caf50; font-size: 14px;">Customer Receives:</div>
              <div class="info-row">
                <span class="info-label" style="color: #4caf50;">Vehicle Downgrade Credit</span>
                <span class="info-value" style="color: #4caf50;">${formatCurrency(Math.abs(adjustmentFee))}</span>
              </div>
              <div style="margin: 16px 0; padding: 16px; background-color: #e8f5e9; border-radius: 4px;">
                <div class="info-row" style="border: none; margin: 0;">
                  <span style="font-weight: 700; font-size: 18px; color: #2e7d32;">Total Refund Amount</span>
                  <span style="font-weight: 700; font-size: 18px; color: #2e7d32;">${formatCurrency(Math.abs(adjustmentFee))}</span>
                </div>
              </div>`}
            </div>`;
          }
          return '';
        })()}

        <div style="margin-top: 24px; padding: 16px; background-color: #f5f5f5; border-radius: 4px; text-align: center;">
          <div style="font-size: 12px; color: #666; margin-bottom: 8px;">Substitution Status</div>
          <span class="status-badge ${
            selectedRequest.status === 'accepted' ? 'status-accepted' : 'status-pending'
          }">
            ${(selectedRequest as any).paymentStatus || selectedRequest.status || 'PENDING'}
          </span>
        </div>

        <div class="footer">
          <p>Thank you for choosing PolarRide!</p>
          <p style="margin-top: 8px;">For support, contact us at support@polarride.com</p>
          <p style="margin-top: 16px; font-size: 12px; color: #999;">
            Generated on ${new Date().toLocaleDateString('en-US', { dateStyle: 'full' })}
          </p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(receiptHtml);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Handle create new substitution
  const handleCreateSubstitution = async () => {
    try {
      if (!newSubstitutionData.bookingId || !newSubstitutionData.substituteVehicleId || !newSubstitutionData.reason) {
        toast.error('Please fill in all required fields');
        return;
      }

      // Note: Backend should validate that substitute vehicle is different from original
      // This is just a client-side reminder
      const adminId = getAdminUserId();

      const response = await substitutionService.createSubstitutionRequest(
        newSubstitutionData.bookingId,
        newSubstitutionData.substituteVehicleId,
        newSubstitutionData.reason,
        adminId
      );

      if (response.success) {
        toast.success('Substitution request created successfully');
        setOpenNewSubstitution(false);
        setNewSubstitutionData({ bookingId: '', substituteVehicleId: '', reason: '' });

        // Refresh list - wrapped in try-catch to prevent crashes
        try {
          await fetchSubstitutionRequests();
        } catch (refreshErr) {
          console.error('Error refreshing after create:', refreshErr);
          // Don't show error to user, data was created successfully
        }
      } else {
        toast.error(response.error || 'Failed to create request');
      }
    } catch (err) {
      console.error('Error creating request:', err);
      toast.error('An error occurred while creating the request');
    }
  };

  // Helper functions
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'accepted': return 'success';
      case 'pending': return 'warning';
      case 'declined':
      case 'rejected': return 'error';
      case 'expired': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Calculate tab counts from analytics
  const tabCounts = {
    all: analytics?.overview?.totalRequests || 0,
    pending: analytics?.overview?.pending || 0,
    accepted: analytics?.overview?.accepted || 0,
    declined: analytics?.overview?.declined || 0
  };

  return (
    <Box>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Bookings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          &gt;
        </Typography>
        <Typography variant="body2" color="primary.main">
          Substitution Requests
        </Typography>
      </Box>

      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Vehicle Substitution Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchSubstitutionRequests();
              fetchAnalytics();
            }}
            disabled={isLoading}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenNewSubstitution(true)}
            sx={{ textTransform: 'none' }}
          >
            New Substitution
          </Button>
        </Box>
      </Box>

      {/* Status Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
          <Tab label={`All Requests (${tabCounts.all})`} />
          <Tab label={`Pending (${tabCounts.pending})`} />
          <Tab label={`Accepted (${tabCounts.accepted})`} />
          <Tab label={`Declined (${tabCounts.declined})`} />
        </Tabs>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} lg={8}>
          {/* Loading State */}
          {isLoading && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="body1" color="text.secondary">
                Loading substitution requests...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Empty State */}
          {!isLoading && !error && substitutionRequests.length === 0 && (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <SwapHoriz sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Substitution Requests Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedTab === 0
                    ? 'There are no substitution requests at the moment.'
                    : `There are no ${selectedTab === 1 ? 'pending' : selectedTab === 2 ? 'accepted' : 'declined'} requests.`
                  }
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Host/Admin Initiated Substitution Requests */}
          {!isLoading && !error && substitutionRequests.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Substitution Requests ({pagination.total})
              </Typography>

              {substitutionRequests.map((request) => {
                const isSameVehicle = request.originalVehicle.id === request.substituteVehicle.id;
                return (
              <Card key={request.id} sx={{ mb: 3, border: isSameVehicle ? '2px solid' : 'none', borderColor: isSameVehicle ? 'error.main' : 'transparent' }}>
                <CardContent>
                  {/* Same Vehicle Warning */}
                  {isSameVehicle && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Invalid Substitution Request
                      </Typography>
                      <Typography variant="body2">
                        The replacement vehicle is the same as the original vehicle. This request should not have been created.
                      </Typography>
                    </Alert>
                  )}

                  {/* Request Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Booking {request.bookingId}
                      </Typography>
                      {request.requestedBy && request.requestedByType && (
                        <Typography variant="body2" color="text.secondary">
                          Requested by: {request.requestedBy.name || 'Unknown'} ({request.requestedByType})
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(request.createdAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {request.adminOverride && (
                        <Tooltip title={`Overridden by admin: ${request.adminOverride.overrideReason}`}>
                          <Chip
                            label="Admin Override"
                            size="small"
                            color="secondary"
                            icon={<ManageAccounts />}
                          />
                        </Tooltip>
                      )}
                      <Chip
                        label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        color={getStatusColor(request.status) as any}
                        variant={request.status === 'pending' ? 'filled' : 'outlined'}
                      />
                    </Box>
                  </Box>

                  {/* Vehicle Comparison */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    {/* Original Vehicle */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                        Original Vehicle
                      </Typography>
                      <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                        {(() => {
                          const { make, model, year } = parseVehicleName(request.originalVehicle.name);
                          return (
                            <>
                              <Box sx={{ height: 120, borderRadius: 1, mb: 2, overflow: 'hidden', position: 'relative' }}>
                                <img
                                  src={request.originalVehicle.image || 'https://via.placeholder.com/300x120/cccccc/666666?text=No+Image'}
                                  alt={request.originalVehicle.name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                                <Box sx={{
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                  color: 'white',
                                  p: 1
                                }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {request.originalVehicle.name}
                                  </Typography>
                                </Box>
                              </Box>
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">Make</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{make}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">Model</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{model}</Typography>
                                </Grid>
                                {year && (
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Year</Typography>
                                    <Typography variant="body2">{year}</Typography>
                                  </Grid>
                                )}
                                {request.originalVehicle.color && (
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Color</Typography>
                                    <Typography variant="body2">{request.originalVehicle.color}</Typography>
                                  </Grid>
                                )}
                                {request.originalVehicle.licensePlate && (
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">License Plate</Typography>
                                    <Typography variant="body2">{request.originalVehicle.licensePlate}</Typography>
                                  </Grid>
                                )}
                                {request.originalVehicle.verificationStatus && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Status</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                                      <Typography variant="body2">{request.originalVehicle.verificationStatus}</Typography>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </>
                          );
                        })()}
                      </Paper>
                    </Grid>

                    {/* Substitute Vehicle */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                        Substitute Vehicle
                      </Typography>
                      <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                        {(() => {
                          const { make, model, year } = parseVehicleName(request.substituteVehicle.name);
                          return (
                            <>
                              <Box sx={{ height: 120, borderRadius: 1, mb: 2, overflow: 'hidden', position: 'relative' }}>
                                <img
                                  src={request.substituteVehicle.image || 'https://via.placeholder.com/300x120/cccccc/666666?text=No+Image'}
                                  alt={request.substituteVehicle.name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                                <Box sx={{
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                  color: 'white',
                                  p: 1
                                }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {request.substituteVehicle.name}
                                  </Typography>
                                </Box>
                              </Box>
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">Make</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{make}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">Model</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{model}</Typography>
                                </Grid>
                                {year && (
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Year</Typography>
                                    <Typography variant="body2">{year}</Typography>
                                  </Grid>
                                )}
                                {request.substituteVehicle.color && (
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Color</Typography>
                                    <Typography variant="body2">{request.substituteVehicle.color}</Typography>
                                  </Grid>
                                )}
                                {request.substituteVehicle.licensePlate && (
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">License Plate</Typography>
                                    <Typography variant="body2">{request.substituteVehicle.licensePlate}</Typography>
                                  </Grid>
                                )}
                                {request.substituteVehicle.verificationStatus && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Status</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Schedule sx={{ color: 'warning.main', fontSize: 16 }} />
                                      <Typography variant="body2">{request.substituteVehicle.verificationStatus}</Typography>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </>
                          );
                        })()}
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Substitution Reason */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Substitution Reason
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {request.reason}
                    </Typography>
                  </Box>

                  {/* Guest Information */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Guest Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {request.guest.name || 'Unknown'}
                      {request.guest.email && ` • ${request.guest.email}`}
                      {request.guest.phone && ` • ${request.guest.phone}`}
                    </Typography>
                  </Box>

                  {/* Host Information */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Host Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {request.host.name || 'Unknown'}
                      {request.host.email && ` • ${request.host.email}`}
                      {request.host.phone && ` • ${request.host.phone}`}
                    </Typography>
                  </Box>

                  {/* Guest Response Status */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Guest Response
                    </Typography>
                    {request.guestResponse?.responded ? (
                      <Alert severity={request.guestResponse.decision === 'accept' ? 'success' : 'error'} sx={{ mb: 2 }}>
                        Guest {request.guestResponse.decision}ed on {formatDate(request.guestResponse.responseDate!)}
                      </Alert>
                    ) : (
                      <>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          Waiting for guest response
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                          Guest must respond by: {new Date(request.guestResponseDeadline).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Admin Override Notice */}
                  {request.adminOverride && (
                    <Box sx={{ mb: 3 }}>
                      <Alert severity="info">
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Admin Override Applied
                        </Typography>
                        <Typography variant="body2">
                          Action: {request.adminOverride.action === 'approve' ? 'Approved' : 'Rejected'} by admin
                        </Typography>
                        <Typography variant="body2">
                          Reason: {request.adminOverride.overrideReason}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(request.adminOverride.overriddenAt)}
                        </Typography>
                      </Alert>
                    </Box>
                  )}

                  {/* Action Buttons - Only show for pending requests */}
                  {request.status === 'pending' && !request.adminOverride && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircle />}
                        onClick={() => {
                          const reason = prompt('Enter reason for approving this substitution (optional):');
                          if (reason !== null) {
                            handleOverride(request.id, 'approve', reason || 'Approved by admin');
                          }
                        }}
                      >
                        Force Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<Cancel />}
                        onClick={() => {
                          const reason = prompt('Enter reason for rejecting this substitution (required):');
                          if (reason && reason.trim()) {
                            handleOverride(request.id, 'reject', reason);
                          } else if (reason !== null) {
                            toast.error('Rejection reason is required');
                          }
                        }}
                      >
                        Force Reject
                      </Button>
                    </Box>
                  )}

                  {/* Payment Link and Receipt Buttons - Show for all requests */}
                  <Box sx={{ display: 'flex', gap: 2, mt: request.status === 'pending' && !request.adminOverride ? 2 : 0 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Payment />}
                      onClick={() => handleGeneratePaymentLink(request)}
                    >
                      Payment Link
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Print />}
                      onClick={() => handlePrintReceipt(request)}
                    >
                      Print Receipt
                    </Button>
                  </Box>
                </CardContent>
              </Card>
                );
              })}
            </Box>
          )}

          {/* Pagination (if needed) */}
          {!isLoading && !error && pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Page {pagination.page} of {pagination.totalPages} • {pagination.total} total requests
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Sidebar - Analytics */}
        <Grid item xs={12} lg={4}>
          {/* Analytics Stats */}
          {analytics && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Analytics Overview
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Total Requests</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {analytics.overview?.totalRequests || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Acceptance Rate</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {(analytics.overview?.acceptanceRate || 0).toFixed(1)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Avg Response Time</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {(analytics.overview?.averageResponseTime || 0).toFixed(1)}h
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Admin Overrides</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                      {analytics.adminOverrides?.length || 0}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Top Reasons
                </Typography>
                {(analytics.commonReasons || []).slice(0, 3).map((reason, index) => (
                  <Box key={index} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {(reason.reason || 'N/A').substring(0, 30)}{(reason.reason || '').length > 30 ? '...' : ''}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {(reason.percentage || 0).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          {/* User Trust & Directory - Keep as is or remove */}
          {/* Removing old mock Recent Requests table */}
        </Grid>
      </Grid>

      {/* New Substitution Dialog */}
      <Dialog open={openNewSubstitution} onClose={() => setOpenNewSubstitution(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Substitution Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
            Create a substitution request on behalf of a host. The guest will be notified and given 24 hours to respond.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Booking ID"
                placeholder="Enter booking ID"
                value={newSubstitutionData.bookingId}
                onChange={(e) => setNewSubstitutionData(prev => ({ ...prev, bookingId: e.target.value }))}
                required
                helperText="The UUID of the booking that needs vehicle substitution"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Substitute Vehicle ID"
                placeholder="Enter substitute vehicle ID"
                value={newSubstitutionData.substituteVehicleId}
                onChange={(e) => setNewSubstitutionData(prev => ({ ...prev, substituteVehicleId: e.target.value }))}
                required
                helperText="The UUID of the replacement vehicle (must be different from the original vehicle)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Reason for Substitution"
                placeholder="Explain why the vehicle needs to be substituted..."
                value={newSubstitutionData.reason}
                onChange={(e) => setNewSubstitutionData(prev => ({ ...prev, reason: e.target.value }))}
                required
                helperText="Provide a clear explanation that will be sent to the guest"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenNewSubstitution(false);
            setNewSubstitutionData({ bookingId: '', substituteVehicleId: '', reason: '' });
          }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateSubstitution}>
            Create Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Dialog - Simplified */}
      <Dialog open={receiptDialogOpen} onClose={() => { setReceiptDialogOpen(false); setSelectedRequest(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Print sx={{ fontSize: 24 }} />
          <Typography variant="h6">Receipt</Typography>
        </DialogTitle>
        <DialogContent>{selectedRequest && <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}><Typography><strong>Booking:</strong> {selectedRequest.bookingId}</Typography><Typography><strong>Guest:</strong> {selectedRequest.guest.name}</Typography></Box>}</DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setReceiptDialogOpen(false)}>Close</Button><Button variant="contained" startIcon={<Print />} onClick={handlePrint}>Print</Button></DialogActions>
      </Dialog>

      {/* Payment Link Dialog - Simplified */}
      <Dialog open={paymentLinkDialogOpen} onClose={() => setPaymentLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><CheckCircle color="success" /> Payment Link</DialogTitle>
        <DialogContent>{paymentLinkData && <Stack spacing={2} sx={{ mt: 1 }}><Typography><strong>Customer:</strong> {paymentLinkData.customerName}</Typography><Typography><strong>Amount:</strong> ₦{paymentLinkData.amount.toLocaleString()}</Typography><Box sx={{ p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}><Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>{paymentLinkData.paymentUrl}</Typography><Button size="small" variant="outlined" startIcon={<ContentCopy />} onClick={async () => { await navigator.clipboard.writeText(paymentLinkData.paymentUrl); toast.success('Copied!'); }} fullWidth sx={{ mt: 1 }}>Copy</Button></Box></Stack>}</DialogContent>
        <DialogActions><Button onClick={() => setPaymentLinkDialogOpen(false)} variant="contained">Done</Button></DialogActions>
      </Dialog>
    </Box>
  );
};
